<template>
    <div class="site-search-inner-container">

        <div class="search-header search-header-main">
            <div class="container">
                <div class="decorative-line"></div>

                <h1 class="search-heading-1">Search Results</h1>

                <div class="ais-SearchBox">
                    <form action="" role="search" novalidate="novalidate" class="ais-SearchBox-form" @submit.prevent="submitSearch">
                        <div class="input-wrapper">
                            <div class="icon-container">
                                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 21L16.1667 16.1667M18.7778 9.88889C18.7778 14.7981 14.7981 18.7778 9.88889 18.7778C4.97969 18.7778 1 14.7981 1 9.88889C1 4.97969 4.97969 1 9.88889 1C14.7981 1 18.7778 4.97969 18.7778 9.88889Z" stroke="#222222" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <input type="search" v-model="query" autocorrect="off" autocapitalize="off" autocomplete="off" spellcheck="false" required="required" maxlength="512" aria-label="Search" placeholder="Search here…" class="ais-SearchBox-input" @keyup="triggerSearchDebounce">
                            <button type="reset" title="Clear" class="ais-SearchBox-reset">
                                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M1.00002 15.1421L15.1422 1M15.1421 15.1421L1 1" stroke="#00313C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <button type="submit" title="Search" class="ais-SearchBox-submit btn btn-primary btn-gold">Search</button>
                    </form>
                </div>

                <div class="mobile-section-nav">
                    <div class="dropdown">
                      <button class="btn btn-primary btn-navy dropdown-toggle" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        Jump to:
                      </button>
                      <div class="dropdown-menu" id="dropdownMenu" aria-labelledby="dropdownMenuButton">
                        <div class="dropdown-item" @click="goToSection('providers')">Providers</div>
                        <div class="dropdown-item" @click="goToSection('locations')">Locations</div>
                        <div class="dropdown-item" @click="goToSection('mayo')">Patient Education</div>
                        <div class="dropdown-item" @click="goToSection('jobs')">Jobs</div>
                        <div class="dropdown-item" @click="goToSection('articles')">Magazine Articles</div>
                      </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="search-form-columns" ref="searchContents">
            <div class="container">

                <div class="search-form-columns-inner">

                    <div class="secondary-search-column">

                        <div class="secondary-search-group" ref="searchContentsProviders">
                            <div class="secondary-group-heading">
                                <h3 class="search-subheading">Doctors & Providers</h3>
                            </div>

                            <div class="secondary-search-results-outer">
                                <transition name="fade" mode="out-in">
                                    <div class="loading-indicator" v-if="loading.providers"><span>Searching...</span></div>
                                </transition>

                                <div class="secondary-search-results">

                                    <div class="secondary-search-result provider" :class="{ 'no-link': !result.epic_url }" @click.prevent="selectRecord(result, 'providers')" v-for="result in results.providers">
                                        <div class="thumbnail-column">
                                            <div class="thumbnail">
                                                <img v-if="result.photo" :src="result.photo" :alt="result.name">
                                                <img v-else src="https://monument.health/wp-content/uploads/2021/04/provider-default.jpeg" :alt="result.name">
                                            </div>
                                        </div>
                                        <div class="details">
                                            <div class="result-name" v-text="result.name"></div>
                                            <div class="result-services" v-text="formatArrayDisplay(result.services, 1)"></div>
                                        </div>
                                    </div>

                                </div>

                                <div class="secondary-search-results-footer">
                                    <a :href="providersViewAllUrl" class="group-all-link">View All</a>
                                    <div class="search-results-count" v-text="resultsDisplayProviders"></div>
                                </div>

                            </div>
                        </div>

                        <div class="secondary-search-group" ref="searchContentsLocations">
                            <div class="secondary-group-heading">
                                <h3 class="search-subheading">Locations</h3>
                            </div>

                            <div class="secondary-search-results-outer">
                                <transition name="fade" mode="out-in">
                                    <div class="loading-indicator" v-if="loading.locations"><span>Searching...</span></div>
                                </transition>

                                <div class="secondary-search-results">

                                    <div class="secondary-search-result location" :class="{ 'no-link': !result.epic_url }" @click="selectRecord(result, 'locations')" v-for="result in results.locations">
                                        <div class="thumbnail-column">
                                            <div class="thumbnail" :style="'background-image: url(' + result.image + ')'">
                                            </div>
                                        </div>
                                        <div class="details">
                                            <div class="result-name" v-text="result.name"></div>
                                        </div>
                                    </div>

                                </div>

                            </div>

                            <div class="secondary-search-results-footer">
                                <a :href="locationsViewAllUrl" class="group-all-link">View All</a>
                                <div class="search-results-count" v-text="resultsDisplayLocations"></div>
                            </div>
                        </div>

                        <div class="secondary-search-group" ref="searchContentsMayo">
                            <div class="secondary-group-heading">
                                <h3 class="search-subheading">Resources</h3>
                            </div>

                            <div class="secondary-search-results-outer">
                                <transition name="fade" mode="out-in">
                                    <div class="loading-indicator" v-if="loading.mayo"><span>Searching...</span></div>
                                </transition>

                                <div class="secondary-search-results">

                                    <a :href="'/mayo-clinic-health-information-library/article/?mayo-id=' + result.contentId" class="secondary-search-result simple" @click="selectRecord(result, 'mayo')" v-for="result in results.mayo" target="_blank">
                                        <div class="result-name" v-text="result.title"></div>
                                        <div class="result-details">
                                            <span v-text="result.contentType"></span>
                                            <span class="divider">|</span>
                                            <span v-text="formatDateDisplay(result.updateDate)"></span>
                                        </div>
                                    </a>

                                </div>

                            </div>

                            <div class="secondary-search-results-footer">
                                <a :href="'/mayo-clinic-health-information-library/search/?mayo_q=' + query" class="group-all-link">View All</a>
                                <div class="search-results-count" v-text="resultsDisplayMayo"></div>
                            </div>
                        </div>

                        <div class="secondary-search-group" ref="searchContentsJobs">
                            <div class="secondary-group-heading">
                                <h3 class="search-subheading">Jobs</h3>
    <!--                            <div class="search-results-count" v-text="resultsDisplayJobs"></div>-->

    <!--                            <a :href="'/careers/?query=' + query + '#content-section-7'" class="group-all-link btn btn-more">All</a>-->
                            </div>

                            <div class="secondary-search-results-outer">
                                <transition name="fade" mode="out-in">
                                    <div class="loading-indicator" v-if="loading.jobs"><span>Searching...</span></div>
                                </transition>

                                <div class="secondary-search-results">

                                    <div class="secondary-search-result simple" @click="selectRecord(result, 'jobs')" v-for="result in results.jobs">
                                        <div class="result-name" v-text="result.name"></div>
                                        <div class="result-details">
                                            <span v-text="result.location[0].name"></span>
                                            <span class="divider">|</span>
                                            <span v-text="result.job_code"></span>
                                            <span class="divider">|</span>
<!--                                            <span v-text="formatDateDisplayCreated(result.created)"></span>-->
                                        </div>
                                    </div>

                                </div>

                                <div class="secondary-search-results-footer">
                                    <a href="/careers/" class="group-all-link">View All</a>
                                    <div class="search-results-count" v-text="resultsDisplayJobs"></div>
                                </div>
                            </div>
                        </div>



<!--                      <div class="secondary-search-group" ref="searchContentsArticles">-->
<!--                        <div class="secondary-group-heading">-->
<!--                          <h3 class="search-subheading">Article Results</h3>-->
<!--                          <div class="search-results-count" v-text="resultsDisplayArticles"></div>-->

<!--                          <a href="/magazine/" class="group-all-link btn btn-more">All</a>-->
<!--                        </div>-->

<!--                        <div class="secondary-search-results-outer">-->
<!--                          <transition name="fade" mode="out-in">-->
<!--                            <div class="loading-indicator" v-if="loading.articles"><span>Searching...</span></div>-->
<!--                          </transition>-->

<!--                          <div class="secondary-search-results">-->
<!--                            <div class="secondary-search-result article" @click.prevent="selectRecord(result, 'articles')" v-for="result in results.articles">-->
<!--                              <div class="details">-->
<!--                                <div class="result-name" v-text="result.name"></div>-->
<!--                              </div>-->
<!--                            </div>-->
<!--                          </div>-->
<!--                        </div>-->
<!--                      </div>-->



                    </div>

                    <div class="main-search-column">

                        <div class="secondary-group-heading">
                            <h3 class="search-subheading">Site Results</h3>

                            <div class="search-results-count" v-text="resultsDisplaySite"></div>
                        </div>

                        <div class="main-search-results">

                            <transition name="fade" mode="out-in">
                                <div class="loading-indicator" v-if="loading.site"><span>Searching...</span></div>
                            </transition>

                            <div class="main-search-result" @click="selectRecord(result, 'site')" v-for="result in results.site">
                                <div class="result-name">{{ result.name }}</div>
                                <div class="result-link">{{ result.url }}</div>
                                <div class="result-excerpt" v-if="result.snippet_content">{{ decodeHTMLEntities2(result.snippet_content) }}</div>
                                <div class="result-excerpt" v-else>{{ result.excerpt }}</div>
                            </div>

                        </div>

                        <div class="search-results-pagination">
<!--                            <ais-pagination-->
<!--                              :show-first="true"-->
<!--                              :show-previous="true"-->
<!--                              :show-next="true"-->
<!--                              :show-last="true"-->
<!--                              :padding="10"-->
<!--                              :total-pages="4"-->
<!--                              :class-names="'test'"-->
<!--                            />-->
                            <div class="pagination-navigation">
                                <button :disabled="!pagination.site.prev" @click="prevPageSiteSearch" class="pagination-prev">
                                    <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.5" d="M5 9.5L1 5.5L5 1.5" stroke="#222222" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>

                                <div class="pagination-page-selection">
                                    <ul>
                                        <li v-for="page in sitePages"
                                            :key="page"
                                            :class="{ 'active': page === pagination.site.currentPage }">
                                            <button @click="goToPage(page)">{{ page }}</button>
                                        </li>
                                    </ul>
                                </div>

                                <button :disabled="!pagination.site.next" @click="nextPageSiteSearch" class="pagination-next">
                                    <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 9.5L5 5.5L1 1.5" stroke="#222222" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                            </div>

                            <div class="search-results-count" v-text="resultsDisplaySite"></div>
                        </div>

                    </div>

                </div>

            </div>

        </div>

<!--        <div class="search-results-pagination pagination-desktop-only">-->
<!--            <button :disabled="!pagination.site.prev" @click="prevPageSiteSearch" class="btn btn-back btn-back-gold">Previous</button>-->
<!--            <button :disabled="!pagination.site.next" @click="nextPageSiteSearch" class="btn btn-more">Next</button>-->
<!--        </div>-->

    </div>
</template>

<script>
    // import algoliasearch from 'algoliasearch';

    // const aa = require('search-insights');
    // const client = algoliasearch('O4J2BVWP7X', '25e82b9ff49bfc98eeaea844742d9654');

    const algoliaPrefix = (window.ajax_global && window.ajax_global.algolia_prefix) ? window.ajax_global.algolia_prefix : 'prod_';

    export default {

    props: ['query_in'],

    data() {
        return {
            query: '',
            algoliaPrefix,
            previousQuery: '',
            userSearchId: '',
            mainSearchTab: 'site',
            loading: {
                site: false,
                mayo: false,
                providers: false,
                locations: false,
                jobs: false,
                articles: false
            },
            queryId: {
                site: 0,
                providers: 0,
                locations: 0,
                jobs: 0,
                articles: 0
            },
            results: {
                site: [],
                mayo: [],
                providers: [],
                locations: [],
                jobs: [],
                articles: []
            },
            totalResults: {
                site: 0,
                mayo: 0,
                providers: 0,
                locations: 0,
                jobs: 0,
                articles: 0
            },
            page: {
                site: 1,
                mayo: 1
            },
            totalPages: {
                site: 0,
                mayo: 0
            },
            pagination: {
                site: {
                    prev: false,
                    pageCount: 0,
                    currentPage: 0,
                    next: false
                },
                mayo: {
                    prev: false,
                    next: false
                }
            },
            monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
        }
    },

    mounted() {

        this.query = this.query_in;

        this.userSearchId = /SESS\w*ID=([^;]+)/i.test(document.cookie) ? RegExp.$1 : false;

        this.$nextTick(function () {
            this.initialSearch();
        });

    },

    components: {},

    watch:{

        query: function (newValue) {
            window.updateUrlParameter('query', newValue);
        }

    },

    computed: {

        sitePages: function () {
            const current = this.pagination.site.currentPage,
                first = Math.max(1, current - 2),
                last = Math.min(this.pagination.site.pageCount, current + 2);
            const pages = [];
            for (let i = first; i <= last; i++) {
                pages.push(i);
            }
            return pages;
        },

        resultsDisplaySite: function () {
            if(this.totalResults.site > 12) {
                let startCount = (this.page.site * 12) - 11,
                    endCount = ((this.page.site * 12) <= this.totalResults.site) ? (this.page.site * 12) : this.totalResults.site;

                return 'Showing ' + startCount + '-' + endCount + ' of ' + this.totalResults.site + ' results';
            } else {
                return 'Showing ' + this.totalResults.site + ' results';
            }
        },

        resultsDisplayMayo: function () {
            if(this.totalResults.mayo > 3) {
                return 'Showing 1-3 of ' + this.totalResults.mayo + ' results';
            } else {
                return 'Showing ' + this.totalResults.mayo + ' results';
            }
      },
        
        providersViewAllUrl: function () {
            return this.externalSearchUrl('https://providers.monument.health');
        },

        locationsViewAllUrl: function () {
            return this.externalSearchUrl('https://locations.monument.health');
        },

        resultsDisplayProviders: function () {
            if(this.totalResults.providers > 3) {
                return 'Showing 1-3 of ' + this.totalResults.providers + ' results';
            } else {
                return 'Showing ' + this.totalResults.providers + ' results';
            }
        },

        resultsDisplayLocations: function () {
            if(this.totalResults.locations > 3) {
                return 'Showing 1-3 of ' + this.totalResults.locations + ' results';
            } else {
                return 'Showing ' + this.totalResults.locations + ' results';
            }
        },

        resultsDisplayJobs: function () {
            if(this.totalResults.jobs > 3) {
                return 'Showing 1-3 of ' + this.totalResults.jobs + ' results';
            } else {
                return 'Showing ' + this.totalResults.jobs + ' results';
            }
        },

        resultsDisplayArticles: function () {
          if(this.totalResults.articles > 3) {
            return 'Showing 1-3 of ' + this.totalResults.articles + ' results';
          } else {
            return 'Showing ' + this.totalResults.articles + ' results';
          }
        },

    },

    methods: {

        triggerSearchDebounce: _.debounce(function () {
            this.fullSearch();
        }, 500),

        submitSearch: function () {
            this.fullSearch();
        },

        fullSearch: function () {
            this.initialSearch();
        },

        nextPageSiteSearch: function () {

            if(this.page.site < this.totalPages.site) {
                this.page.site = this.page.site + 1;
            }

            this.resetScrollPosition();
            this.siteSearch();

        },

        goToPage: function (page) {
            this.page.site = page;
            this.resetScrollPosition();
            this.siteSearch();
        },

        prevPageSiteSearch: function () {

            if(this.page.site > 1) {
                this.page.site = this.page.site - 1;
            } else {
                alert('no more previous pages');
            }

            this.resetScrollPosition();
            this.siteSearch();

        },

        resetScrollPosition: function () {
            this.$refs.searchContents.scrollIntoView();
        },

        paginationCheckSite: function () {

            this.pagination.site.prev = (this.page.site > 1);
            this.pagination.site.next = (this.totalPages.site > 1 && this.page.site < this.totalPages.site);

        },

        trySearch: function () {
            _.debounce(this.fullSearch, 1000);
        },

        initialSearch: function () {

            let _this = this;

            // if(!this.query.length) return true;

            this.mayoSearch();

            this.loading.site = true;
            this.loading.providers = true;
            this.loading.locations = true;
            this.loading.jobs = true;
            this.loading.articles = true;

            this.page.site = 1;
            this.pagination.site.currentPage = 1;

            this.page.mayo = 1;

            this.previousQuery = this.query;

            let data = new FormData();
            data.append('action', 'search_initial');
            data.append('query', this.query);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {

                    if(typeof response.data.data.site.results !== 'undefined') _this.results.site = response.data.data.site.results;
                    if(typeof response.data.data.site.total !== 'undefined') {
                        _this.pagination.site.pageCount = Math.ceil(response.data.data.site.total / 12);
                        _this.totalResults.site = response.data.data.site.total;
                    }
                    if(typeof response.data.data.site.pages !== 'undefined') {
                        _this.pagination.site.currentPage = 1;
                        _this.totalPages.site = response.data.data.site.pages;
                    }

                    if(typeof response.data.data.providers.results !== 'undefined') _this.results.providers = response.data.data.providers.results;
                    if(typeof response.data.data.providers.total !== 'undefined') _this.totalResults.providers = response.data.data.providers.total;

                    if(typeof response.data.data.locations.results !== 'undefined') _this.results.locations = response.data.data.locations.results;
                    if(typeof response.data.data.locations.total !== 'undefined') _this.totalResults.locations = response.data.data.locations.total;

                    if(typeof response.data.data.jobs.results !== 'undefined') _this.results.jobs = response.data.data.jobs.results;
                    if(typeof response.data.data.jobs.total !== 'undefined') _this.totalResults.jobs = response.data.data.jobs.total;

                    if(typeof response.data.data.articles.results !== 'undefined') _this.results.articles = response.data.data.articles.results;
                    if(typeof response.data.data.articles.total !== 'undefined') _this.totalResults.articles = response.data.data.articles.total;

                    setTimeout(function () {
                        _this.loading.site = false;
                        _this.loading.providers = false;
                        _this.loading.locations = false;
                        _this.loading.jobs = false;
                        _this.loading.articles = false;
                    }, 300);

                    _this.paginationCheckSite();

                });

        },

        siteSearch: function () {

            this.loading.site = true;

            let _this = this;

            if(this.query !== this.previousQuery) {
                this.page.site = 1;
            }

            this.previousQuery = this.query;

            let data = new FormData();
            data.append('action', 'search_site');
            data.append('query', this.query);
            data.append('page', this.page.site);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {

                    // console.log(response.data);

                    if(typeof response.data.data.results !== 'undefined') _this.results.site = response.data.data.results;
                    if(typeof response.data.data.total !== 'undefined') {
                        _this.totalResults.site = response.data.data.total;
                        _this.pagination.site.pageCount = Math.ceil(_this.totalResults.site / 12);
                    }
                    if(typeof response.data.data.pages !== 'undefined') {
                        _this.totalPages.site = response.data.data.pages;
                        _this.pagination.site.currentPage = _this.page.site;
                    }

                    setTimeout(function () { _this.loading.site = false; }, 300);

                    _this.paginationCheckSite();

                });

        },

        mayoSearch: function () {

            this.loading.mayo = true;

            let _this = this;

            let data = new FormData();
            data.append('action', 'search_mayo');
            data.append('query', this.query);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {

                    if(typeof response.data.data.results !== 'undefined') _this.results.mayo = response.data.data.results;
                    if(typeof response.data.data.total !== 'undefined') _this.totalResults.mayo = response.data.data.total;

                    setTimeout(function () { _this.loading.mayo = false; }, 300);

                });

        },

        providersSearch: function () {

            this.loading.providers = true;

            let _this = this;

            let data = new FormData();
            data.append('action', 'search_providers');
            data.append('query', this.query);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {

                    if(typeof response.data.data.results !== 'undefined') _this.results.providers = response.data.data.results;
                    if(typeof response.data.data.total !== 'undefined') _this.totalResults.providers = response.data.data.total;

                    setTimeout(function () { _this.loading.providers = false; }, 300);

                });

        },

        locationsSearch: function () {

            this.loading.locations = true;

            let _this = this;

            let data = new FormData();
            data.append('action', 'search_locations');
            data.append('query', this.query);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {

                    if(typeof response.data.data.results !== 'undefined') _this.results.locations = response.data.data.results;
                    if(typeof response.data.data.total !== 'undefined') _this.totalResults.locations = response.data.data.total;

                    setTimeout(function () { _this.loading.locations = false; }, 300);

                });

        },

        jobsSearch: function () {

            this.loading.jobs = true;

            let _this = this;

            let data = new FormData();
            data.append('action', 'search_jobs');
            data.append('query', this.query);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {

                    if(typeof response.data.data.results !== 'undefined') _this.results.jobs = response.data.data.results;
                    if(typeof response.data.data.total !== 'undefined') _this.totalResults.jobs = response.data.data.total;

                    setTimeout(function () { _this.loading.jobs = false; }, 300);

                });

        },


        articlesSearch: function () {

          this.loading.articles = true;

          let _this = this;

          let data = new FormData();
          data.append('action', 'search_articles');
          data.append('query', this.query);

          axios.post('/wp-admin/admin-ajax.php', data)
              .then(function (response) {

                if(typeof response.data.data.results !== 'undefined') _this.results.articles = response.data.data.results;
                if(typeof response.data.data.total !== 'undefined') _this.totalResults.articles = response.data.data.total;

                setTimeout(function () { _this.loading.articles = false; }, 300);

              });

        },

        selectRecord: function (result, index) {

            let url = '',
                indexID = '',
                eventName = '';

            switch(index) {
                case 'locations':
                    url = result.epic_url;
                    indexID = this.algoliaPrefix + 'LOCATIONS';
                    // eventName = 'main_search_clicked_locations';
                    break;
                case 'providers':
                    url = result.epic_url;
                    indexID = this.algoliaPrefix + 'PROVIDERS';
                    // eventName = 'main_search_clicked_providers';
                    break;
                case 'site':
                    url = result.url;
                    indexID = this.algoliaPrefix + 'SITE';
                    break;
                case 'jobs':
                    url = result.url;
                    indexID = this.algoliaPrefix + 'JOBS';
                    break;
                case 'articles':
                    url = result.url;
                    indexID = this.algoliaPrefix + 'ARTICLES';
                    break;
                default:
                    url = result.url;
                    break;
            }

            // Epic records often have no URL.
            if (!url) return;

            // console.log()

            /*$.post('https://insights.algolia.io/1/events', {
                'X-Algolia-Application-Id': 'O4J2BVWP7X',
                'X-Algolia-API-Key': '25e82b9ff49bfc98eeaea844742d9654',
                'eventName': 'main_search_click',
                'index': indexID,
                'queryID': result.id,
                'objectIDs': [result.id]
            }, function (result) {
                console.log(result);
            });*/

            // send click through
            // aa('clickedObjectIDs', {
            //     userToken: this.userSearchId, // required for Node.js
            //     index: indexID,
            //     eventName: 'main_search_click',
            //     objectIDs: [result.id]
            // });

            let data = new FormData();
            data.append('action', 'click_result');
            data.append('index', indexID);
            data.append('resultId', result.id);
            data.append('userToken', this.userSearchId);

            axios.post('/wp-admin/admin-ajax.php', data)
                .then(function (response) {
                    console.log(response);
                });

            // window.location.href = url;
            window.open(url, "_blank");

        },

        formatDateDisplay: function (dateInput) {

            let dateTime = new Date(dateInput);

            if(!dateTime) return '';

            return this.monthNames[dateTime.getMonth()] + ' ' + dateTime.getDay() + ', ' + dateTime.getFullYear();

        },

        formatDateDisplayCreated: function (dateInput) {

            let dateTime = new Date(dateInput);

            if(this.isToday(dateTime)) {
                return 'Posted Today';
            } else if(this.isYesterday(dateTime)) {
                return 'Posted Yesterday';
            } else {
                return 'Posted ' + this.monthNames[dateTime.getMonth()] + ' ' + dateTime.getDay() + ', ' + dateTime.getFullYear();
            }

        },

        isToday: function (someDate) {
            const today = new Date()
            return someDate.getDate() === today.getDate() &&
                someDate.getMonth() === today.getMonth() &&
                someDate.getFullYear() === today.getFullYear()
        },

        isYesterday: function (someDate) {
            const today = new Date();
            const yesterday = new Date(today);

            yesterday.setDate(yesterday.getDate() - 1);

            return someDate.getDate() === yesterday.getDate() &&
                someDate.getMonth() === yesterday.getMonth() &&
                someDate.getFullYear() === yesterday.getFullYear()
        },

        externalSearchUrl: function (base) {

            let query = (this.query || '').trim();

            return query ? base + '/results?searchString=' + encodeURIComponent(query) : base;
        },

        formatArrayDisplay: function (array, maxLength = 0) {

            let returnString = '';

            if(maxLength > 0 && array.length > maxLength) {
                for (let i = 0; i < maxLength; i++) {
                    if(returnString === "") {
                        returnString += this.decodeHTMLEntities(array[i]);
                    } else {
                        returnString += ', ' + this.decodeHTMLEntities(array[i]);
                    }
                }
                // returnString += ', + more';
            } else {
                for (let i = 0; i < array.length; i++) {
                    if(returnString === "") {
                        returnString += this.decodeHTMLEntities(array[i]);
                    } else {
                        returnString += ', ' + this.decodeHTMLEntities(array[i]);
                    }
                }
            }

            return returnString;

        },

        decodeHTMLEntities: function (text) {
            var entities = [
                ['amp', '&'],
                ['apos', '\''],
                ['#x27', '\''],
                ['#x2F', '/'],
                ['#39', '\''],
                ['#47', '/'],
                ['lt', '<'],
                ['gt', '>'],
                ['nbsp', ' '],
                ['quot', '"']
            ];

            for (var i = 0, max = entities.length; i < max; ++i)
                text = text.replace(new RegExp('&'+entities[i][0]+';', 'g'), entities[i][1]);

            return text;
        },

        decodeHTMLEntities2: function (text) {

            var map = {
                '&amp;': '&',
                '&#038;': "&",
                '&lt;': '<',
                '&gt;': '>',
                '&quot;': '"',
                '&#039;': "'",
                '&#8217;': "’",
                '&#8216;': "‘",
                '&#8211;': "–",
                '&#8212;': "—",
                '&#8230;': "…",
                '&#8221;': '”'
            };

            return text.replace(/\&[\w\d\#]{2,5}\;/g, function(m) { return map[m]; });

        },

        goToSection: function (section) {

            if(section === "providers") {
                this.$refs.searchContentsProviders.scrollIntoView();
            } else if(section === "locations") {
                this.$refs.searchContentsLocations.scrollIntoView();
            } else if(section === "mayo") {
                this.$refs.searchContentsMayo.scrollIntoView();
            } else if(section === "jobs") {
                this.$refs.searchContentsJobs.scrollIntoView();
            }
            else if(section === "articles") {
              this.$refs.searchContentsArticles.scrollIntoView();
            }

        }

    }

}
</script>
